// 🚀 [Phase v13.55 — 2026-07-22] Page admin destinataires SMS automatiques.
//
// Route : `/admin/sms-destinataires`
// Permissions : propriétaire + adjoint admin (guard backend).
//
// Fonctionnalités :
//   - Liste tous les events SMS groupés par category (commandes / alertes / notifications)
//   - Pour chaque event : toggle enabled, liste destinataires configurés, aperçu résolution
//   - Ajout destinataire (employé ou numéro manuel)
//   - Retrait destinataire
//   - Envoi SMS de test (owner uniquement)
//   - Historique des changements (audit_log)

import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { AlertTriangle, MessageSquare, Bell, ShoppingCart, Plus, Trash2, Send, User, Phone, ChevronDown, ChevronUp, X } from 'lucide-react';
import { useAuth } from '@/lib/AuthContext';
import { estProprietaire, aLeRole } from '@/lib/roles'; // 👥 E3b — rôles multiples

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_META = {
  commandes: { label: 'Commandes', icon: ShoppingCart, color: 'text-orange-600', bg: 'bg-orange-50' },
  alertes: { label: 'Alertes', icon: AlertTriangle, color: 'text-red-600', bg: 'bg-red-50' },
  notifications: { label: 'Notifications', icon: Bell, color: 'text-blue-600', bg: 'bg-blue-50' },
};

function EventCard({ cfg, employees, onRefresh, isOwner }) {
  const [expanded, setExpanded] = useState(false);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const meta = CATEGORY_META[cfg.category] || CATEGORY_META.notifications;
  const Icon = meta.icon;

  const toggleEnabled = async () => {
    try {
      await axios.put(`${API_URL}/api/admin/sms-destinataires/${cfg.event_key}`, {
        enabled: !cfg.enabled,
      });
      toast.success(`${cfg.enabled ? 'Désactivé' : 'Activé'} : ${cfg.event_label}`);
      onRefresh();
    } catch (e) {
      toast.error(`Erreur : ${e.response?.data?.detail || e.message}`);
    }
  };

  const removeRecipient = async (idx) => {
    if (!window.confirm('Retirer ce destinataire ?')) return;
    const newRecipients = [...cfg.recipients];
    newRecipients.splice(idx, 1);
    try {
      await axios.put(`${API_URL}/api/admin/sms-destinataires/${cfg.event_key}`, {
        recipients: newRecipients.map(r => ({
          type: r.type,
          employee_id: r.employee_id,
          phone: r.phone,
          phone_override: r.phone_override,
          label: r.label,
        })),
      });
      toast.success('Destinataire retiré');
      onRefresh();
    } catch (e) {
      toast.error(`Erreur : ${e.response?.data?.detail || e.message}`);
    }
  };

  const testSend = async () => {
    if (!window.confirm(`Envoyer un SMS de test à ${cfg.resolved_preview.length} destinataire(s) ?\nCoût Twilio applicable.`)) return;
    try {
      const r = await axios.post(`${API_URL}/api/admin/sms-destinataires/${cfg.event_key}/test-send`);
      toast.success(`Test envoyé — ${r.data.count} destinataire(s)`);
    } catch (e) {
      toast.error(`Erreur test : ${e.response?.data?.detail || e.message}`);
    }
  };

  return (
    <Card
      className={`transition-all ${cfg.enabled ? '' : 'opacity-60'}`}
      data-testid={`sms-event-${cfg.event_key}`}
    >
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3 flex-1 min-w-0">
            <div className={`p-2 rounded-lg ${meta.bg} flex-shrink-0`}>
              <Icon className={`w-5 h-5 ${meta.color}`} />
            </div>
            <div className="flex-1 min-w-0">
              <CardTitle className="text-base font-semibold leading-tight">{cfg.event_label}</CardTitle>
              <p className="text-xs text-slate-500 mt-1">{cfg.event_description}</p>
              <code className="text-[10px] text-slate-400 mt-1 block">event_key: {cfg.event_key}</code>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <Switch
              checked={cfg.enabled}
              onCheckedChange={toggleEnabled}
              data-testid={`sms-toggle-enabled-${cfg.event_key}`}
              aria-label={`${cfg.enabled ? 'Désactiver' : 'Activer'} l'event ${cfg.event_key}`}
            />
            <Badge variant={cfg.enabled ? 'default' : 'secondary'} className="text-[10px]">
              {cfg.enabled ? 'Actif' : 'Off'}
            </Badge>
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-0 space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-medium text-slate-700">
            {cfg.recipients?.length || 0} destinataire(s) configuré(s)
          </span>
          <span className="text-slate-400">•</span>
          <span className="text-xs text-slate-500">
            {cfg.resolved_preview?.length || 0} résolu(s) actuellement
          </span>
          {cfg.resolved_preview?.some(r => r.resolved_from === 'fallback') && (
            <Badge variant="destructive" className="text-[10px]" data-testid={`sms-fallback-badge-${cfg.event_key}`}>
              ⚠ Fallback propriétaire actif
            </Badge>
          )}
        </div>

        {/* Liste destinataires */}
        <div className="space-y-1.5">
          {(cfg.recipients || []).map((r, idx) => (
            <div
              key={idx}
              className="flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-50 border border-slate-200"
              data-testid={`sms-recipient-${cfg.event_key}-${idx}`}
            >
              {r.type === 'employee' ? (
                <>
                  <User className="w-4 h-4 text-slate-500 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-800 truncate">
                      {r.employee_nom || '(introuvable)'}
                      {!r.employee_actif && (
                        <Badge variant="destructive" className="ml-2 text-[9px]">inactif</Badge>
                      )}
                    </p>
                    <p className="text-[11px] text-slate-500">
                      {r.employee_role || 'rôle inconnu'} · {r.phone_override_masked || r.employee_phone_masked || '(sans téléphone)'}
                    </p>
                  </div>
                </>
              ) : (
                <>
                  <Phone className="w-4 h-4 text-slate-500 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-800 truncate">{r.label}</p>
                    <p className="text-[11px] text-slate-500">{r.phone_masked || r.phone}</p>
                  </div>
                </>
              )}
              <Button
                size="sm"
                variant="ghost"
                onClick={() => removeRecipient(idx)}
                className="text-red-500 hover:text-red-700 hover:bg-red-50 h-7 w-7 p-0 flex-shrink-0"
                data-testid={`sms-recipient-remove-${cfg.event_key}-${idx}`}
                aria-label="Retirer ce destinataire"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </div>
          ))}
          {(cfg.recipients?.length || 0) === 0 && (
            <p className="text-xs text-slate-400 italic px-3 py-2">
              Aucun destinataire configuré — fallback propriétaire actif.
            </p>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2 pt-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => setAddModalOpen(true)}
            data-testid={`sms-add-recipient-${cfg.event_key}`}
            className="gap-1.5"
          >
            <Plus className="w-3.5 h-3.5" /> Ajouter destinataire
          </Button>
          {isOwner && cfg.enabled && (cfg.resolved_preview?.length || 0) > 0 && (
            <Button
              size="sm"
              variant="outline"
              onClick={testSend}
              data-testid={`sms-test-send-${cfg.event_key}`}
              className="gap-1.5 text-blue-700 border-blue-300 hover:bg-blue-50"
            >
              <Send className="w-3.5 h-3.5" /> Envoyer test
            </Button>
          )}
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setHistoryOpen(!historyOpen)}
            data-testid={`sms-history-toggle-${cfg.event_key}`}
            className="gap-1.5 text-slate-500"
          >
            {historyOpen ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
            Historique ({cfg.audit_log?.length || 0})
          </Button>
        </div>

        {historyOpen && (
          <div className="border-t border-slate-200 pt-3 space-y-1.5 text-xs" data-testid={`sms-history-${cfg.event_key}`}>
            {(cfg.audit_log || []).slice(-8).reverse().map((entry, i) => (
              <div key={i} className="flex items-start gap-2 text-slate-600">
                <span className="text-slate-400 font-mono text-[10px]">
                  {new Date(entry.at).toLocaleString('fr-CA', { dateStyle: 'short', timeStyle: 'short' })}
                </span>
                <span className="flex-1">
                  <strong>{entry.by}</strong> — {entry.action}
                </span>
              </div>
            ))}
          </div>
        )}
      </CardContent>

      <AddRecipientModal
        open={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        eventKey={cfg.event_key}
        eventLabel={cfg.event_label}
        currentRecipients={cfg.recipients || []}
        employees={employees}
        onSaved={() => {
          setAddModalOpen(false);
          onRefresh();
        }}
      />
    </Card>
  );
}

function AddRecipientModal({ open, onClose, eventKey, eventLabel, currentRecipients, employees, onSaved }) {
  const [type, setType] = useState('employee');
  const [selectedEmployeeId, setSelectedEmployeeId] = useState('');
  const [phoneOverride, setPhoneOverride] = useState('');
  const [phone, setPhone] = useState('');
  const [label, setLabel] = useState('');
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) {
      setType('employee');
      setSelectedEmployeeId('');
      setPhoneOverride('');
      setPhone('');
      setLabel('');
      setSearch('');
    }
  }, [open]);

  const filteredEmployees = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return employees.slice(0, 40);
    return employees.filter(e =>
      `${e.prenom || ''} ${e.nom || ''}`.toLowerCase().includes(q) ||
      (e.email || '').toLowerCase().includes(q)
    ).slice(0, 40);
  }, [employees, search]);

  const save = async () => {
    const newRecipients = [...currentRecipients].map(r => ({
      type: r.type,
      employee_id: r.employee_id,
      phone: r.phone,
      phone_override: r.phone_override,
      label: r.label,
    }));

    if (type === 'employee') {
      if (!selectedEmployeeId) {
        toast.error('Sélectionne un employé');
        return;
      }
      newRecipients.push({
        type: 'employee',
        employee_id: selectedEmployeeId,
        phone_override: phoneOverride.trim() || null,
      });
    } else {
      if (!phone.trim()) {
        toast.error('Numéro requis');
        return;
      }
      if (!label.trim()) {
        toast.error('Description requise');
        return;
      }
      newRecipients.push({
        type: 'phone',
        phone: phone.trim(),
        label: label.trim(),
      });
    }

    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/admin/sms-destinataires/${eventKey}`, {
        recipients: newRecipients,
      });
      toast.success('Destinataire ajouté');
      onSaved();
    } catch (e) {
      toast.error(`Erreur : ${e.response?.data?.detail || e.message}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="sms-add-modal">
        <DialogHeader>
          <DialogTitle>Ajouter un destinataire</DialogTitle>
          <p className="text-xs text-slate-500 mt-1">Event : {eventLabel}</p>
        </DialogHeader>
        <div className="space-y-4">
          <RadioGroup value={type} onValueChange={setType}>
            <div className="flex items-center gap-2">
              <RadioGroupItem value="employee" id="type-employee" data-testid="sms-add-type-employee" />
              <Label htmlFor="type-employee" className="cursor-pointer">Un employé (par nom)</Label>
            </div>
            <div className="flex items-center gap-2">
              <RadioGroupItem value="phone" id="type-phone" data-testid="sms-add-type-phone" />
              <Label htmlFor="type-phone" className="cursor-pointer">Un numéro de téléphone manuel</Label>
            </div>
          </RadioGroup>

          {type === 'employee' && (
            <div className="space-y-2">
              <Label className="text-sm">Rechercher un employé</Label>
              <Input
                placeholder="Nom ou courriel..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                data-testid="sms-add-employee-search"
              />
              <div className="max-h-56 overflow-y-auto space-y-1 border rounded-lg p-1">
                {filteredEmployees.map(e => (
                  <button
                    key={e.id}
                    type="button"
                    onClick={() => setSelectedEmployeeId(e.id)}
                    className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${
                      selectedEmployeeId === e.id
                        ? 'bg-blue-100 text-blue-900 font-medium'
                        : 'hover:bg-slate-100 text-slate-700'
                    }`}
                    data-testid={`sms-add-employee-option-${e.id}`}
                  >
                    <div className="font-medium">{e.prenom} {e.nom}</div>
                    <div className="text-[11px] text-slate-500">{e.role || e.type_employe} · {e.email || 'pas de courriel'}</div>
                  </button>
                ))}
                {filteredEmployees.length === 0 && (
                  <p className="text-xs text-slate-400 italic px-3 py-4 text-center">Aucun résultat</p>
                )}
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Numéro de téléphone override (optionnel)</Label>
                <Input
                  placeholder="+15145551234 (laisser vide pour utiliser le numéro de l'employé)"
                  value={phoneOverride}
                  onChange={e => setPhoneOverride(e.target.value)}
                  data-testid="sms-add-phone-override"
                />
              </div>
            </div>
          )}

          {type === 'phone' && (
            <div className="space-y-3">
              <div className="space-y-1">
                <Label className="text-sm">Numéro de téléphone (format E.164)</Label>
                <Input
                  placeholder="+15145551234"
                  value={phone}
                  onChange={e => setPhone(e.target.value)}
                  data-testid="sms-add-phone"
                />
                <p className="text-[11px] text-slate-500">Format canadien : +1 suivi de 10 chiffres. Ex : +15145551234.</p>
              </div>
              <div className="space-y-1">
                <Label className="text-sm">Description (obligatoire)</Label>
                <Input
                  placeholder="Ex : Numéro d'urgence chantier"
                  value={label}
                  onChange={e => setLabel(e.target.value)}
                  data-testid="sms-add-label"
                />
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="sms-add-cancel">Annuler</Button>
          <Button onClick={save} disabled={saving} data-testid="sms-add-save">
            {saving ? 'Enregistrement...' : 'Ajouter'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function SmsDestinatairesAdmin() {
  const { user } = useAuth();
  const [configs, setConfigs] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('all');
  const [search, setSearch] = useState('');

  // Cat H — identité Propriétaire (page globalement Owner-only via backend Vague 1 ; l'affichage des boutons "Envoyer test" / "Migrer" est un raccourci UI supplémentaire).
  const isOwner = estProprietaire(user);

  const loadConfigs = async () => {
    try {
      const r = await axios.get(`${API_URL}/api/admin/sms-destinataires`);
      setConfigs(r.data.configs || []);
    } catch (e) {
      toast.error(`Erreur chargement : ${e.response?.data?.detail || e.message}`);
    } finally {
      setLoading(false);
    }
  };

  const loadEmployees = async () => {
    try {
      const r = await axios.get(`${API_URL}/api/employees?include_owners=true`);
      setEmployees(r.data.employees || []);
    } catch (e) {
      // silencieux
    }
  };

  useEffect(() => {
    loadConfigs();
    loadEmployees();
  }, []);

  const filteredConfigs = useMemo(() => {
    let out = configs;
    if (activeCategory !== 'all') {
      out = out.filter(c => c.category === activeCategory);
    }
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      out = out.filter(c =>
        c.event_label.toLowerCase().includes(q) ||
        c.event_key.toLowerCase().includes(q) ||
        (c.event_description || '').toLowerCase().includes(q)
      );
    }
    return out;
  }, [configs, activeCategory, search]);

  const stats = useMemo(() => {
    const total = configs.length;
    const active = configs.filter(c => c.enabled).length;
    const withFallback = configs.filter(c =>
      c.enabled && c.resolved_preview?.some(r => r.resolved_from === 'fallback')
    ).length;
    return { total, active, withFallback };
  }, [configs]);

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto p-4">
        <p className="text-sm text-slate-500">Chargement…</p>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6 p-4" data-testid="sms-destinataires-page">
      {/* En-tête */}
      <div className="flex items-start gap-3">
        <div className="p-3 rounded-xl bg-blue-50">
          <MessageSquare className="w-7 h-7 text-blue-600" />
        </div>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-slate-900">Destinataires SMS automatiques</h1>
          <p className="text-sm text-slate-500 mt-1">
            Configure qui reçoit les SMS pour chaque event automatique du système. Change facilement en cas de démission ou de changement de responsable.
          </p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Card><CardContent className="pt-4">
          <p className="text-xs text-slate-500 uppercase font-medium">Events configurés</p>
          <p className="text-2xl font-bold text-slate-900" data-testid="sms-stat-total">{stats.total}</p>
        </CardContent></Card>
        <Card><CardContent className="pt-4">
          <p className="text-xs text-slate-500 uppercase font-medium">Actifs</p>
          <p className="text-2xl font-bold text-emerald-600" data-testid="sms-stat-active">{stats.active}</p>
        </CardContent></Card>
        <Card><CardContent className="pt-4">
          <p className="text-xs text-slate-500 uppercase font-medium">En fallback propriétaire</p>
          <p className="text-2xl font-bold text-orange-600" data-testid="sms-stat-fallback">{stats.withFallback}</p>
          {stats.withFallback > 0 && (
            <p className="text-[11px] text-orange-500 mt-1">Aucun destinataire configuré — ajoutez-en un</p>
          )}
        </CardContent></Card>
      </div>

      {/* Recherche + filtre category */}
      <div className="flex flex-col sm:flex-row gap-3">
        <Input
          placeholder="Rechercher un event..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="flex-1"
          data-testid="sms-search-input"
        />
      </div>

      <Tabs value={activeCategory} onValueChange={setActiveCategory}>
        <TabsList className="grid grid-cols-4 w-full sm:w-auto">
          <TabsTrigger value="all" data-testid="sms-tab-all">Tous</TabsTrigger>
          <TabsTrigger value="commandes" data-testid="sms-tab-commandes">Commandes</TabsTrigger>
          <TabsTrigger value="alertes" data-testid="sms-tab-alertes">Alertes</TabsTrigger>
          <TabsTrigger value="notifications" data-testid="sms-tab-notifications">Notifications</TabsTrigger>
        </TabsList>

        <TabsContent value={activeCategory} className="space-y-3 mt-4">
          {filteredConfigs.length === 0 ? (
            <Card><CardContent className="pt-6">
              <p className="text-sm text-slate-500 text-center py-4">Aucun event ne correspond aux filtres.</p>
            </CardContent></Card>
          ) : (
            filteredConfigs.map(cfg => (
              <EventCard
                key={cfg.event_key}
                cfg={cfg}
                employees={employees}
                onRefresh={loadConfigs}
                isOwner={isOwner}
              />
            ))
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
